import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BellRing, CalendarClock, XCircle } from 'lucide-react';
import { instructorApi } from '../../api/instructor';
import { InstructorLesson } from '../../types';
import dayjs from '../../lib/dates';
import { Button, ConfirmDialog, TextField, useToast, type MenuEntry } from '../charte';
import { Sheet } from '../shell/Sheet';

/** ISO (UTC) → valeur d'un <input type="datetime-local"> (heure locale). */
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const failure = (e: unknown, fallback: string) => (e instanceof Error && e.message) || fallback;

/**
 * Actions secondaires d'une leçon côté moniteur (menu « ··· ») : la reporter (nouveaux horaires),
 * envoyer un rappel à l'élève, l'annuler (confirmée). Rien pour un créneau ouvert, une leçon
 * terminée, annulée ou dont l'horaire de fin est passé.
 */
export function useLessonActions(lesson: InstructorLesson): { entries: MenuEntry[]; dialogs: ReactNode } {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [rescheduling, setRescheduling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [startsAt, setStartsAt] = useState(() => toLocalInput(lesson.startsAt));
  const [endsAt, setEndsAt] = useState(() => toLocalInput(lesson.endsAt));
  const [notify, setNotify] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['today'] });
    void queryClient.invalidateQueries({ queryKey: ['planning'] });
  };

  const cancelMutation = useMutation({
    mutationFn: () => instructorApi.cancelLesson(lesson.id),
    onSuccess: () => {
      refresh();
      setCancelling(false);
      toast('Leçon annulée, l’élève est prévenu.');
    },
  });

  const rescheduleMutation = useMutation({
    mutationFn: () =>
      instructorApi.rescheduleLesson(lesson.id, {
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        notify,
      }),
    onSuccess: () => {
      refresh();
      setRescheduling(false);
      toast('Leçon reportée.');
    },
    onError: (e) => setError(failure(e, 'Report impossible.')),
  });

  const remindMutation = useMutation({
    mutationFn: () => instructorApi.remindLesson(lesson.id),
    onSuccess: () => toast("Rappel envoyé à l'élève."),
    onError: (e) => toast(failure(e, 'Rappel impossible.')),
  });

  const allowed = !lesson.isOpenSlot && lesson.status === 'scheduled' && !dayjs().isAfter(dayjs(lesson.endsAt));
  if (!allowed) return { entries: [], dialogs: null };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
      setError('L’heure de fin doit être postérieure à l’heure de début.');
      return;
    }
    rescheduleMutation.mutate();
  };

  const entries: MenuEntry[] = [
    {
      label: 'Reporter',
      icon: CalendarClock,
      onSelect: () => {
        setStartsAt(toLocalInput(lesson.startsAt));
        setEndsAt(toLocalInput(lesson.endsAt));
        setError(null);
        setRescheduling(true);
      },
    },
    {
      label: remindMutation.isPending ? 'Envoi du rappel…' : "Rappel à l'élève",
      icon: BellRing,
      disabled: remindMutation.isPending,
      onSelect: () => remindMutation.mutate(),
    },
    { kind: 'separator' },
    { label: 'Annuler la leçon', icon: XCircle, danger: true, onSelect: () => setCancelling(true) },
  ];

  const formId = `reporter-${lesson.id}`;
  const dialogs = (
    <>
      {rescheduling && (
        <Sheet
          title="Reporter la leçon"
          onClose={() => setRescheduling(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setRescheduling(false)}>
                Retour
              </Button>
              <Button type="submit" form={formId} variant="primary" className="ml-auto" loading={rescheduleMutation.isPending}>
                {rescheduleMutation.isPending ? 'Report en cours…' : 'Confirmer le report'}
              </Button>
            </>
          }
        >
          <form id={formId} onSubmit={onSubmit} className="space-y-4">
            <TextField label="Nouveau début" type="datetime-local" required value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="h-11 text-[15px]" />
            <TextField label="Nouvelle fin" type="datetime-local" required value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="h-11 text-[15px]" />
            <label className="flex items-center gap-2.5 text-sm text-body">
              <input type="checkbox" className="h-4 w-4 accent-primary" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
              Notifier l'élève du nouvel horaire
            </label>
            {error && <p role="alert" className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">{error}</p>}
          </form>
        </Sheet>
      )}
      <ConfirmDialog
        open={cancelling}
        title="Annuler cette leçon ?"
        tone="danger"
        confirmLabel="Annuler la leçon"
        pending={cancelMutation.isPending}
        error={cancelMutation.isError ? failure(cancelMutation.error, 'Annulation impossible.') : undefined}
        onConfirm={() => cancelMutation.mutate()}
        onCancel={() => {
          setCancelling(false);
          cancelMutation.reset();
        }}
      >
        L'élève sera prévenu de l'annulation.
      </ConfirmDialog>
    </>
  );
  return { entries, dialogs };
}
